import { useCallback } from 'react';
import { ITooltipProps } from './tooltip/useSingleChartDataTooltip';
import { useChartContext } from '../ChartContext';
import {
  ExpressionModeKey,
  FunnelModeKey,
  ITimeToConvertInput,
  QueryType,
  SimpleCohortTypeKey,
  TimeToConvertOperatorKey,
  TimeUnit,
} from '../../../utils/apollo/graphqlSchema';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { getCohortPagePath } from '../../../utils/routes';
import { saveCachedCohortAndOpenModalThunk } from '../../Builder/Cohorts/CohortRow/utils';
import { findMeasureIdByMeasureKeyInBuilderInfo } from '../../Builder/utils/builderUtils';
import { IBreakdownOverallStatus, MaybeNil } from '../../../utils/interfaces';
import { useAnalysisPrepareCohort } from '../../../utils/createCohort';
import { ILineMetaNewExpression, ILineMetaNewFunnel } from '../interfaces';
import prepareCachedCohort from '../../../utils/createCohort/prepareCachedCohort';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../redux/hooks';
import {
  parseCohortDurationStartDate,
  parseFunnelLookForwardWindow,
  parseRetentionLookForwardWindow,
} from '../../../redux/slices/oldBuilder/utils';
import { IRetentionTooltipProps } from './tooltip/useRetentionTooltip';

interface IProps {
  analysisId: string;
}

const usePrepareCohort = ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    metadata: { startDate, endDate, query, isBreakdownOverall, timeUnit },
    displayedSlices,
    chartData,
  } = useChartContext();
  const handlePrepareAnalysis = useAnalysisPrepareCohort(analysisId);
  const handleCreateCohortFromExpression = useCallback(
    async (
      tooltipProps: ITooltipProps,
      lineMeta: ILineMetaNewExpression[string],
      isExecuteAfterRedirect: boolean,
    ) => {
      assertCondition(query?.__typename === 'ExpressionQuery');

      const { date, line } = tooltipProps;
      assertCondition(chartData.queryType === QueryType.Expression);

      const measureId =
        query.__typename === 'ExpressionQuery'
          ? findMeasureIdByMeasureKeyInBuilderInfo(query, line.measureKey)
          : '';
      // if we need to prepare new analysis, we need to use new analysis id
      let id = analysisId;
      let newMeasureId;
      if (
        isBreakdownOverall !== IBreakdownOverallStatus.NoBreakdown &&
        isBreakdownOverall !== IBreakdownOverallStatus.NoBreakdownCompare
      ) {
        const response = await handlePrepareAnalysis(
          lineMeta.sliceKey,
          measureId,
        );
        const analysis = response.data?.analysisPrepareCohort?.entity?.analysis;
        newMeasureId =
          response.data?.analysisPrepareCohort?.entity?.expressionMeasure?.id;
        assertNotNil(analysis);
        id = analysis.id;
      }

      let start = startDate;
      let end = endDate;
      if (chartData.xAxis?.__typename === 'QueryModeXAxis') {
        const cohortDate = chartData.xAxis.cohortDates.find(
          (cohortDate) => cohortDate.date === date,
        );
        if (cohortDate) {
          start = cohortDate.interval.start;
          end = cohortDate.interval.end;
        }
      }

      // get bucket in HistogramMode
      let histogramBucket: MaybeNil<string> = null;
      const xAxisIndex = chartData.dates.findIndex((d) => d === date);
      const slice = displayedSlices.find(
        (slice) => slice.key === lineMeta.sliceKey,
      );
      if (
        // this is a histogram with breakdown, assumes All Time
        chartData.xAxis?.__typename === 'HistogramModeXAxis' &&
        xAxisIndex < chartData.xAxis.buckets.length
      ) {
        // assert time unit, otherwise start / end date won't be correct to
        // represent the cohort
        assertCondition(timeUnit === TimeUnit.AllTime);
        histogramBucket = chartData.xAxis.buckets[xAxisIndex];
      } else if (
        query.expressionMode.key === ExpressionModeKey.Histogram &&
        slice
      ) {
        // this is histogram but w/o breakdown
        assertCondition(slice.originalHint.length === 1);
        histogramBucket = slice.originalHint[0].value;
      }

      const cachedCohortData = prepareCachedCohort({
        analysisId: id,
        // if no new measureId, we can simply use original one since no need to prepare new analysis
        measureId: newMeasureId ?? measureId,
        referToNewMeasure: !!newMeasureId,
        type: SimpleCohortTypeKey.Expression,
        startDate: start,
        endDate: end,
        builderInfo: query,
        histogramBucket,
        isExecuteAfterRedirect,
      });
      dispatch(
        saveCachedCohortAndOpenModalThunk(
          cachedCohortData,
          isExecuteAfterRedirect,
          analysisId,
        ),
      );

      if (isExecuteAfterRedirect) {
        navigate(getCohortPagePath());
      }
    },
    [
      analysisId,
      query,
      displayedSlices,
      chartData,
      startDate,
      endDate,
      dispatch,
      navigate,
      handlePrepareAnalysis,
      isBreakdownOverall,
      timeUnit,
    ],
  );

  const handleCreateCohortFromFunnel = useCallback(
    async (
      tooltipProps: ITooltipProps,
      lineMeta: ILineMetaNewFunnel[string],
      isExecuteAfterRedirect: boolean,
    ) => {
      assertCondition(query?.__typename === 'FunnelQuery');
      assertCondition(chartData.queryType === QueryType.Funnel);
      const isTimeToConvertMode =
        query.mode?.key === FunnelModeKey.TimeToConvert;
      const { date, line, startDate, endDate } = tooltipProps;

      let stepIndex = 0;
      if (isTimeToConvertMode) {
        stepIndex = chartData.funnelModeSlices.findIndex(
          (modeStep) => modeStep.key === line.measureKey,
        );
      } else {
        stepIndex = chartData.measureControls.findIndex(
          (control) => control.key === line.measureKey,
        );
      }
      assertCondition(stepIndex >= 0);

      let start = startDate;
      let end = endDate;
      let timeToConvertInput: MaybeNil<ITimeToConvertInput> = null;
      if (chartData.xAxis?.__typename === 'FunnelConversionXAxis') {
        const cohortDate = chartData.xAxis.cohortDates.find(
          (cohortDate) => cohortDate.date === date,
        );
        if (cohortDate) {
          start = cohortDate.interval.start;
          end = cohortDate.interval.end;
        }
      } else if (chartData.xAxis?.__typename === 'FunnelTimeToConvertXAxis') {
        assertNotNil(tooltipProps.xAxisLabelIndex);
        const interval = chartData.xAxis.buckets[tooltipProps.xAxisLabelIndex];
        timeToConvertInput = {
          operator: TimeToConvertOperatorKey.Between,
          timeUnit: chartData.xAxis.timeUnit,
          values: [interval.startInclusive, interval.endExclusive],
        };
      }
      const cohortDurationStart = parseCohortDurationStartDate(
        query,
        startDate,
      );
      const lookForwardWindow = parseFunnelLookForwardWindow(query, null);

      const slice = displayedSlices.find(
        (slice) => slice.key === lineMeta.sliceKey,
      );
      assertNotNil(slice);
      let id = analysisId;

      if (
        isBreakdownOverall !== IBreakdownOverallStatus.NoBreakdown &&
        isBreakdownOverall !== IBreakdownOverallStatus.NoBreakdownCompare &&
        slice
      ) {
        const response = await handlePrepareAnalysis(slice.key);

        const analysis = response.data?.analysisPrepareCohort?.entity?.analysis;
        if (analysis) {
          id = analysis.id;
        }
      }
      const cachedCohortData = prepareCachedCohort({
        analysisId: id,
        type: SimpleCohortTypeKey.Funnel,
        startDate: start,
        endDate: end,
        builderInfo: query,
        index: stepIndex,
        isExecuteAfterRedirect,
        cohortDurationStart,
        lookForwardWindow,
        timeToConvertInput,
      });

      dispatch(
        saveCachedCohortAndOpenModalThunk(
          cachedCohortData,
          isExecuteAfterRedirect,
          analysisId,
        ),
      );

      if (isExecuteAfterRedirect) {
        navigate(getCohortPagePath());
      }
    },
    [
      analysisId,
      query,
      displayedSlices,
      chartData,
      dispatch,
      navigate,
      handlePrepareAnalysis,
      isBreakdownOverall,
    ],
  );

  const handleCreateCohortFromRetention = useCallback(
    async (
      tooltipProps: IRetentionTooltipProps,
      isExecuteAfterRedirect: boolean,
    ) => {
      assertCondition(query?.__typename === 'RetentionQuery');

      const cohortDurationStart = parseCohortDurationStartDate(
        query,
        startDate,
      );
      const lookForwardWindow = parseRetentionLookForwardWindow(
        query.retentionUnit,
        query.timeWindow,
      );

      let start = startDate;
      let end = endDate;

      // Standard cell: use the cohort from the retention defined by the start
      // interval of the cell.
      // IMPORTANT: we use the actualInterval - in case of time unit different
      // than day (e.g. Week, 4W, Month) the first and last startInterval
      // "snaps" to nearest Week/4W/Month start or end which may be beyond
      // the query start / end. In this case, we use actualInterval
      // which is clamped between query start / end to create cohort.
      // See KB-7659 for more details
      if ('actualInterval' in tooltipProps.returnCell) {
        const actualInterval = tooltipProps.returnCell.actualInterval;
        start = actualInterval.start;
        end = actualInterval.end;
      }
      let id = analysisId;
      if (
        isBreakdownOverall !== IBreakdownOverallStatus.NoBreakdown &&
        tooltipProps.sliceKey
      ) {
        const response = await handlePrepareAnalysis(tooltipProps.sliceKey);

        const analysis = response.data?.analysisPrepareCohort?.entity?.analysis;

        if (analysis) {
          id = analysis.id;
        }
      }

      const cachedCohortData = prepareCachedCohort({
        analysisId: id,
        type: SimpleCohortTypeKey.Retention,
        index: tooltipProps.returnIndex,
        startDate: start,
        endDate: end,
        isChurn: tooltipProps.isChurn,
        builderInfo: query,
        isExecuteAfterRedirect,
        cohortDurationStart,
        lookForwardWindow,
      });
      dispatch(
        saveCachedCohortAndOpenModalThunk(
          cachedCohortData,
          isExecuteAfterRedirect,
          analysisId,
        ),
      );

      if (isExecuteAfterRedirect) {
        navigate(getCohortPagePath());
      }
    },
    [
      analysisId,
      query,
      dispatch,
      navigate,
      handlePrepareAnalysis,
      isBreakdownOverall,
      startDate,
      endDate,
    ],
  );

  return {
    handleCreateCohortFromExpression,
    handleCreateCohortFromFunnel,
    handleCreateCohortFromRetention,
  };
};

export default usePrepareCohort;
